import React, { useEffect, useRef } from 'react';

const AuroraCursor = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion preference (applies to both desktop and touch)
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Config (minimal version)
    const config = {
      trailPointCount: 15,       // Reduced from 42
      baseLineWidth: 8,           // Thinner than original
      followSpring: 0.22,
      tailFollow: 0.38,
      maxSparkCount: 40,          // Reduced from 160
      sparksPerMove: [1, 3],      // Fewer sparks
      mintBase: 162,              // HSL hue for mint (#5ae0b5)
      mintLight: 71,              // Lightness %
      mintSat: 61                 // Saturation %
    };

    // Canvas setup
    let cssWidth = 0, cssHeight = 0, dpr = 1;
    
    function resizeCanvas() {
      cssWidth = window.innerWidth;
      cssHeight = window.innerHeight;
      dpr = Math.max(1, Math.min(1.5, window.devicePixelRatio || 1));
      canvas.width = Math.floor(cssWidth * dpr);
      canvas.height = Math.floor(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    window.addEventListener('resize', resizeCanvas, { passive: true });
    resizeCanvas();

    // Touch-only branch (mobile). Desktop continues below.
    if (isMobile) {
      const sparks = [];
      const TOUCH_BURST_COUNT = 12;
      const TOUCH_MOVE_THROTTLE_MS = 40;
      const TOUCH_SPARK_MAX_LIFE_MS = 800;
      let lastTouchSpawn = 0;
      let animationFrameId;

      function spawnTouchBurst(x, y) {
        const createdAt = performance.now();
        for (let i = 0; i < TOUCH_BURST_COUNT && sparks.length < config.maxSparkCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = 0.6 + Math.random() * 1.4;
          sparks.push({
            x, y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            createdAt,
            maxLifeMs: TOUCH_SPARK_MAX_LIFE_MS,
            hueOffset: Math.random() * 30 - 15
          });
        }
      }

      function handleTouchStart(ev) {
        for (const t of ev.changedTouches) spawnTouchBurst(t.clientX, t.clientY);
      }

      function handleTouchMove(ev) {
        const now = performance.now();
        if (now - lastTouchSpawn < TOUCH_MOVE_THROTTLE_MS) return;
        lastTouchSpawn = now;
        for (const t of ev.changedTouches) spawnTouchBurst(t.clientX, t.clientY);
      }

      window.addEventListener('touchstart', handleTouchStart, { passive: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: true });

      function animateTouch() {
        animationFrameId = requestAnimationFrame(animateTouch);
        const now = performance.now();
        ctx.clearRect(0, 0, cssWidth, cssHeight);

        ctx.globalCompositeOperation = 'lighter';
        for (let i = sparks.length - 1; i >= 0; i--) {
          const s = sparks[i];
          s.x += s.vx;
          s.y += s.vy;
          s.vy += 0.015;
          const t = (now - s.createdAt) / s.maxLifeMs;
          if (t >= 1) { sparks.splice(i, 1); continue; }
          const alpha = (1 - t) * 0.32;
          const hue = config.mintBase + s.hueOffset;
          ctx.beginPath();
          ctx.fillStyle = `hsla(${hue}, ${config.mintSat}%, ${config.mintLight}%, ${alpha})`;
          ctx.arc(s.x, s.y, 1.4 + (1 - t) * 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = 'source-over';
      }

      animateTouch();

      return () => {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('resize', resizeCanvas);
        cancelAnimationFrame(animationFrameId);
      };
    }

    // State
    const pointer = { x: cssWidth / 2, y: cssHeight / 2, speed: 0 };
    const trailPoints = Array.from({ length: config.trailPointCount }, () => ({ 
      x: pointer.x, 
      y: pointer.y 
    }));
    const sparks = [];
    let lastMoveTime = performance.now();

    // Pointer tracking
    function handlePointerMove(ev) {
      const x = ev.clientX;
      const y = ev.clientY;
      const now = performance.now();
      const dt = Math.max(8, now - lastMoveTime);
      const dx = x - pointer.x;
      const dy = y - pointer.y;
      const dist = Math.hypot(dx, dy);

      pointer.speed = pointer.speed * 0.85 + (dist / dt) * 0.15 * 16;
      pointer.x = x;
      pointer.y = y;

      // Spawn fewer sparks
      const sparkCount = Math.floor(config.sparksPerMove[0] + Math.random() * 
        (config.sparksPerMove[1] - config.sparksPerMove[0]));
      spawnSparksAt(x, y, sparkCount);
      
      lastMoveTime = now;
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: true });

    // Sparks
    function spawnSparksAt(x, y, count) {
      for (let i = 0; i < count && sparks.length < config.maxSparkCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.5 + Math.random() * 1.2;
        sparks.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          maxLife: 30 + Math.random() * 25,
          hueOffset: Math.random() * 30 - 15
        });
      }
    }

    function updateAndDrawSparks() {
      ctx.globalCompositeOperation = 'lighter';
      
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.x += s.vx;
        s.y += s.vy;
        s.vy += 0.015;
        s.life++;

        const t = s.life / s.maxLife;
        if (t >= 1) {
          sparks.splice(i, 1);
          continue;
        }

        const alpha = (1 - t) * 0.25;
        const hue = config.mintBase + s.hueOffset;
        
        ctx.beginPath();
        ctx.fillStyle = `hsla(${hue}, ${config.mintSat}%, ${config.mintLight}%, ${alpha})`;
        ctx.arc(s.x, s.y, 1 + (1 - t) * 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      
      ctx.globalCompositeOperation = 'source-over';
    }

    // Animation loop
    function animate() {
      requestAnimationFrame(animate);

      // Update trail physics
      trailPoints[0].x += (pointer.x - trailPoints[0].x) * config.followSpring;
      trailPoints[0].y += (pointer.y - trailPoints[0].y) * config.followSpring;
      
      for (let i = 1; i < trailPoints.length; i++) {
        trailPoints[i].x += (trailPoints[i - 1].x - trailPoints[i].x) * config.tailFollow;
        trailPoints[i].y += (trailPoints[i - 1].y - trailPoints[i].y) * config.tailFollow;
      }

      // Clear
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      // Draw sparks
      updateAndDrawSparks();

      // Draw ribbon
      ctx.globalCompositeOperation = 'lighter';
      
      for (let i = 1; i < trailPoints.length; i++) {
        const p0 = trailPoints[i - 1];
        const p1 = trailPoints[i];
        const t = i / (trailPoints.length - 1);

        const width = Math.max(0.4, config.baseLineWidth * (1 - t) * (1 + pointer.speed * 0.12));
        const hue = config.mintBase + (1 - t) * 20;
        const alpha = 0.08 + (1 - t) * 0.18;

        ctx.lineWidth = width;
        ctx.strokeStyle = `hsla(${hue}, ${config.mintSat}%, ${config.mintLight}%, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.stroke();
      }

      // Head glow
      const head = trailPoints[0];
      const headAlpha = 0.25 + Math.min(0.35, pointer.speed * 0.04);
      
      ctx.beginPath();
      ctx.fillStyle = `hsla(${config.mintBase}, ${config.mintSat}%, ${config.mintLight + 5}%, ${headAlpha})`;
      ctx.arc(head.x, head.y, 2.5 + Math.min(5, pointer.speed * 0.6), 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = 'source-over';
    }

    animate();

    // Hide default cursor
    document.body.style.cursor = 'none';

    // Cleanup
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('resize', resizeCanvas);
      document.body.style.cursor = 'default';
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999
      }}
    />
  );
};

export default AuroraCursor;
